import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { 
    FaFileInvoiceDollar, FaEdit, FaFilePdf, FaHistory, FaTrash, FaPlusCircle, FaSearch, FaExclamationCircle, FaCheckCircle, FaEye
} from 'react-icons/fa';
import { 
    fetchFactures, createFacture, updateFacture, deleteFacture, fetchClients, fetchPrestations, exportFacturePDF 
} from '../api'; 

const FacturePage = ({ user }) => {
    const location = useLocation();
    
    const [factures, setFactures] = useState([]);
    const [clients, setClients] = useState([]);
    const [prestations, setPrestations] = useState([]);
    
    const [clientSearch, setClientSearch] = useState('');
    const [currentLines, setCurrentLines] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
    const [dueDate, setDueDate] = useState(''); 
    const [prestationSearchTerm, setPrestationSearchTerm] = useState(''); 
    const [showSuggestions, setShowSuggestions] = useState(false);        
    const [selectedPrestation, setSelectedPrestation] = useState(null);   
    const [remise, setRemise] = useState(0); // 🆕 État pour la remise

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all"); 
    
    const [downloadingPdfId, setDownloadingPdfId] = useState(null);
    const [viewInvoice, setViewInvoice] = useState(null);

    const loadData = async () => {
        setLoading(true);
        try {
            const [resFac, resCli, resPrest] = await Promise.all([
                fetchFactures(), fetchClients(), fetchPrestations()
            ]);
            const facturesDefinitives = (resFac.data || []).filter(f => f.type !== 'avoir' && f.type !== 'proforma');
            setFactures(facturesDefinitives);
            setClients(resCli.data);
            setPrestations(resPrest.data);
        } catch (err) {
            setMessage({ type: 'error', text: 'Erreur de connexion au serveur.' });
        } finally { setLoading(false); }
    };

    useEffect(() => { 
        loadData(); 
        if (location.state && location.state.incomingPrestations) {
            const lines = location.state.incomingPrestations.map(p => ({
                prestationId: p._id, code: p.code, name: p.name, details: p.details || '',
                price: p.price, quantity: 1, total: p.price
            }));
            setCurrentLines(lines);
            window.history.replaceState({}, document.title);
        }
    }, [location]);

    const filteredPrestations = prestationSearchTerm.length > 0
        ? prestations.filter(p => p.name.toLowerCase().includes(prestationSearchTerm.toLowerCase()) || p.code.toLowerCase().includes(prestationSearchTerm.toLowerCase())).slice(0, 50) 
        : [];

    const handleSelectSuggestion = (p) => {
        setSelectedPrestation(p);          
        setPrestationSearchTerm(p.name);    
        setShowSuggestions(false);          
    };

    const handleAddPrestation = () => {
        if (!selectedPrestation) return;
        const newLine = { 
            prestationId: selectedPrestation._id, code: selectedPrestation.code, name: selectedPrestation.name, 
            details: selectedPrestation.details || '', price: Number(selectedPrestation.price), 
            quantity: 1, total: Number(selectedPrestation.price) 
        };
        setCurrentLines([...currentLines, newLine]);
        setSelectedPrestation(null); setPrestationSearchTerm(''); setMessage(null); 
    };

    const updateLine = (idx, field, val) => {
        const newLines = [...currentLines];
        newLines[idx][field] = val;
        if (field === 'price' || field === 'quantity') {
            newLines[idx].total = Number(newLines[idx].price) * Number(newLines[idx].quantity);
        }
        setCurrentLines(newLines);
    };

    const removeLine = (idx) => setCurrentLines(currentLines.filter((_, i) => i !== idx));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!clientSearch || currentLines.length === 0) return;

        const payload = { 
            clientName: clientSearch, lines: currentLines, 
            date: invoiceDate, dueDate: dueDate, type: 'final',
            remise: Number(remise) || 0 // 🆕 Ajout de la remise dans le payload
        };

        try {
            if (editingId) { 
                await updateFacture(editingId, payload); 
                setMessage({ type: 'success', text: '✅ Mise à jour réussie' }); 
            } else { 
                await createFacture(payload); 
                setMessage({ type: 'success', text: '✅ Facture émise avec succès !' }); 
            }
            setEditingId(null); setClientSearch(''); setCurrentLines([]); setDueDate(''); setRemise(0); loadData(); 
        } catch (err) { setMessage({ type: 'error', text: 'Erreur lors de l\'enregistrement.' }); }
    };

    const handleDownloadPDF = async (id, number) => {
        setDownloadingPdfId(id);
        try {
            const res = await exportFacturePDF(id);
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a'); 
            link.href = url;
            link.setAttribute('download', `Facture_${number.replace(/\//g, '-')}.pdf`);
            document.body.appendChild(link); 
            link.click();
            link.remove(); 
            window.URL.revokeObjectURL(url); 
        } catch (err) { 
            setMessage({ type: 'error', text: "Erreur lors du téléchargement du PDF." }); 
        } finally {
            setDownloadingPdfId(null);
        }
    };

    const handleDelete = async (f) => {
        if (f.status === 'payée') {
            setMessage({ type: 'error', text: '⛔ Impossible de supprimer une facture déjà payée.' });
            return;
        }
        if (user?.role !== 'admin') return;
        if (!window.confirm("⚠️ Supprimer cette facture ?")) return;
        try { 
            await deleteFacture(f._id); 
            loadData(); 
            setMessage({type:'success', text:'✅ Supprimée par l\'administrateur.'}); 
        } catch (e) { setMessage({type:'error', text:'Erreur.'}); }
    };

    const startEdit = (f) => {
        if (f.status === 'payée') {
            setMessage({ type: 'error', text: '⛔ Impossible de modifier une facture déjà payée.' });
            return;
        }
        setEditingId(f._id);
        setClientSearch(f.clientName);
        setCurrentLines(f.lines);
        setRemise(f.remise || 0); // 🆕 Chargement de la remise existante
        if(f.date) setInvoiceDate(new Date(f.date).toISOString().split('T')[0]);
        if(f.dueDate) setDueDate(new Date(f.dueDate).toISOString().split('T')[0]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setMessage({ type: 'warning', text: "✏️ MODE ÉDITION" });
    };

    const totalHTBrut = currentLines.reduce((acc, l) => acc + (l.total || 0), 0);
    const numRemise = Number(remise) || 0;
    const totalTTC = Math.max(0, totalHTBrut - numRemise); // Pour éviter un total négatif

    return (
        <div className="container-fluid py-4 px-3 px-md-4">
            
            {/* HEADER */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-2">
                <div>
                    <h4 className="fw-bold mb-0 text-uppercase text-dark" style={{ letterSpacing: '1px' }}>
                        <FaFileInvoiceDollar className="me-2 text-primary"/> FACTURES DÉFINITIVES
                    </h4>
                    <p className="small mb-0 mt-1 text-muted" style={{ letterSpacing: '1px' }}>Gestion et émission des documents officiels</p>
                </div>
            </div>

            {/* MESSAGES D'ALERTE */}
            {message && (
                <div className={`alert py-2 shadow-sm border-0 ${message.type === 'error' ? 'alert-danger' : (message.type === 'warning' ? 'alert-warning' : 'alert-success')}`}>
                    {message.text}
                </div>
            )}

            {/* FORMULAIRE D'ÉMISSION */}
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-header bg-white py-3 fw-bold text-uppercase text-primary" style={{ letterSpacing: '1px' }}>
                    {editingId ? "MODIFIER LA FACTURE" : "ÉMETTRE UNE NOUVELLE FACTURE"}
                </div>
                <div className="card-body p-4">
                    <form onSubmit={handleSubmit}>
                        <div className="row g-3 mb-4">
                            <div className="col-12 col-md-6">
                                <label className="fw-bold small mb-1 text-muted">CLIENT *</label>
                                <input className="form-control form-control-sm" value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} list="clientsListFac" placeholder="Rechercher ou saisir un client..." />
                                <datalist id="clientsListFac">{clients.map(c => <option key={c._id} value={c.name} />)}</datalist>
                            </div>
                            <div className="col-12 col-sm-6 col-md-3">
                                <label className="fw-bold small mb-1 text-muted">DATE ÉMISSION *</label>
                                <input type="date" className="form-control form-control-sm" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
                            </div>
                            <div className="col-12 col-sm-6 col-md-3">
                                <label className="fw-bold small mb-1 text-muted">DATE ÉCHÉANCE</label>
                                <input type="date" className="form-control form-control-sm" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                            </div>
                        </div>

                        {/* RECHERCHE PRESTATION */}
                        <div className="p-3 mb-4 rounded bg-light border">
                            <label className="fw-bold small mb-2 text-primary">RECHERCHER UNE PRESTATION</label>
                            <div className="position-relative">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text bg-white text-muted border-end-0"><FaSearch/></span>
                                    <input type="text" className="form-control border-start-0" placeholder="Nom ou code..." value={prestationSearchTerm}
                                        onChange={(e) => { setPrestationSearchTerm(e.target.value); setShowSuggestions(true); }}
                                        onFocus={() => setShowSuggestions(true)} onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                    />
                                    <button type="button" className={`btn fw-bold px-3 text-white ${selectedPrestation ? 'btn-primary' : 'btn-secondary'}`} onClick={handleAddPrestation} disabled={!selectedPrestation}>
                                        <FaPlusCircle className="me-1" /> AJOUTER
                                    </button>
                                </div>
                                {showSuggestions && prestationSearchTerm && (
                                    <div className="position-absolute w-100 shadow bg-white border rounded mt-1" style={{zIndex: 1000, maxHeight: '200px', overflowY: 'auto'}}>
                                        {filteredPrestations.map(p => (
                                            <div key={p._id} className="p-2 border-bottom cursor-pointer text-dark text-decoration-none bg-light-hover" onMouseDown={() => handleSelectSuggestion(p)} style={{cursor: 'pointer'}}>
                                                <div className="fw-bold">{p.name}</div>
                                                <div className="small text-muted">{p.code} | <span className="fw-bold text-primary">{p.price.toLocaleString()} F</span></div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* TABLEAU DES LIGNES */}
                        <div className="table-responsive mb-4 border rounded">
                            <table className="table table-sm mb-0 align-middle table-hover text-nowrap">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="ps-3 py-2 text-muted" style={{minWidth: '200px'}}>Prestation</th>
                                        <th className="py-2 text-muted" style={{minWidth: '150px'}}>Détails suppl.</th>
                                        <th className="py-2 text-muted" style={{minWidth: '120px'}}>P.U (F)</th>
                                        <th className="py-2 text-muted" style={{minWidth: '80px'}}>Qté</th>
                                        <th className="text-end py-2 text-muted" style={{minWidth: '120px'}}>Total (F)</th>
                                        <th className="text-center py-2 text-muted" style={{minWidth: '50px'}}>#</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentLines.length === 0 ? (
                                        <tr><td colSpan="6" className="text-center py-4 text-muted"><em>Aucune prestation ajoutée.</em></td></tr>
                                    ) : (
                                        currentLines.map((line, idx) => (
                                            <tr key={idx}>
                                                <td className="ps-3 fw-bold text-wrap text-dark" style={{minWidth: '200px'}}>{line.name}</td>
                                                <td><input className="form-control form-control-sm" value={line.details} onChange={(e) => updateLine(idx, 'details', e.target.value)} placeholder="..." /></td>
                                                <td><input type="number" className="form-control form-control-sm text-end" value={line.price} onChange={(e) => updateLine(idx, 'price', e.target.value)} /></td>
                                                <td><input type="number" className="form-control form-control-sm text-center" value={line.quantity} onChange={(e) => updateLine(idx, 'quantity', e.target.value)} /></td>
                                                <td className="text-end fw-bold text-primary">{line.total.toLocaleString()}</td>
                                                <td className="text-center"><button type="button" className="btn btn-sm btn-outline-danger p-1 border-0" onClick={() => removeLine(idx)}><FaTrash size={16}/></button></td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-light">
                                        <td colSpan="4" className="text-end py-2 fw-bold text-muted">SOUS-TOTAL :</td>
                                        <td className="text-end py-2 fw-bold text-secondary">{totalHTBrut.toLocaleString()} F</td>
                                        <td></td>
                                    </tr>
                                    <tr className="bg-light border-bottom">
                                        <td colSpan="4" className="text-end py-2 fw-bold text-muted align-middle">REMISE :</td>
                                        <td className="text-end py-1">
                                            <input 
                                                type="number" 
                                                className="form-control form-control-sm text-end text-danger fw-bold ms-auto" 
                                                style={{maxWidth: '120px'}}
                                                value={remise} 
                                                onChange={(e) => setRemise(Math.max(0, Number(e.target.value)))} 
                                                min="0"
                                            />
                                        </td>
                                        <td></td>
                                    </tr>
                                    <tr className="bg-light">
                                        <td colSpan="4" className="text-end py-3 fw-bold text-dark fs-6">TOTAL GÉNÉRAL :</td>
                                        <td className="text-end fs-5 py-3 fw-bold text-primary">{totalTTC.toLocaleString()} F</td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center pt-3 mt-3 border-top gap-3">
                            <div className="text-muted small fst-italic">* Champs obligatoires</div>
                            <div className="d-flex align-items-center gap-3 mt-3 mt-md-0">
                                {(!clientSearch || currentLines.length === 0) && (
                                    <span className="small fw-bold text-warning">
                                        <FaExclamationCircle className="me-1"/> Informations incomplètes
                                    </span>
                                )}
                                <button 
                                    className={`btn px-4 py-2 fw-bold text-white ${currentLines.length === 0 || !clientSearch ? 'btn-secondary' : 'btn-primary'}`} 
                                    type="submit" 
                                    disabled={currentLines.length === 0 || !clientSearch}
                                >
                                    {editingId ? <><FaEdit className="me-2"/> METTRE À JOUR</> : <><FaCheckCircle className="me-2"/> VALIDER ET ÉMETTRE</>}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* HISTORIQUE */}
            <div className="card shadow-sm border-0 overflow-hidden">
                <div className="card-header bg-light p-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 border-bottom">
                    <span className="fw-bold text-dark" style={{ letterSpacing: '1px' }}><FaHistory className="me-2 text-primary"/>HISTORIQUE</span>
                    <div className="d-flex flex-column flex-sm-row gap-2 w-100 w-md-auto">
                        <select className="form-select form-select-sm w-100 w-sm-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="all">Tous statuts</option>
                            <option value="émise">Émise</option>
                            <option value="payée">Payée</option>
                        </select>
                        <div className="input-group input-group-sm w-100 w-sm-auto">
                            <span className="input-group-text bg-white text-muted border-end-0"><FaSearch /></span>
                            <input className="form-control border-start-0" placeholder="Rechercher n° ou client..." value={search} onChange={(e) => setSearch(e.target.value)} />
                        </div>
                    </div>
                </div>
                <div className="table-responsive" style={{ maxHeight: '400px' }}>
                    <table className="table table-sm table-hover align-middle mb-0 text-nowrap">
                        <thead className="bg-light sticky-top">
                            <tr className="text-uppercase text-muted" style={{ letterSpacing: '1px', fontSize: '0.85rem' }}>
                                <th className="ps-3 py-3 border-bottom-0">N° Facture</th>
                                <th className="py-3 border-bottom-0">Client</th>
                                <th className="py-3 border-bottom-0">Montant TTC</th>
                                <th className="py-3 border-bottom-0">Date</th>
                                <th className="py-3 border-bottom-0 text-center">Statut</th>
                                <th className="pe-3 py-3 text-end border-bottom-0">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {factures.filter(f => (f.number.toLowerCase().includes(search.toLowerCase()) || f.clientName.toLowerCase().includes(search.toLowerCase())) && (statusFilter === 'all' || f.status === statusFilter)).length === 0 && (
                                <tr><td colSpan="6" className="text-center py-4 text-muted">Aucune facture trouvée.</td></tr>
                            )}
                            {factures.filter(f => (f.number.toLowerCase().includes(search.toLowerCase()) || f.clientName.toLowerCase().includes(search.toLowerCase())) && (statusFilter === 'all' || f.status === statusFilter))
                                .map(f => (
                                <tr key={f._id}>
                                    <td className="ps-3 fw-bold text-primary">{f.number}</td>
                                    <td className="text-wrap text-dark fw-bold" style={{minWidth: '150px'}}>{f.clientName}</td>
                                    <td className="fw-bold text-dark">{f.totalTTC?.toLocaleString()} F</td>
                                    <td className="text-muted small">{new Date(f.date).toLocaleDateString()}</td>
                                    <td className="text-center">
                                        <span className={`badge rounded-1 px-2 py-1 border ${f.status === 'payée' ? 'bg-success text-success bg-opacity-10 border-success' : 'bg-warning text-warning-emphasis bg-opacity-10 border-warning'}`}>
                                            {f.status.toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="text-end pe-3">
                                        <button className="btn btn-sm btn-outline-info rounded-1 me-1 py-1 px-2" onClick={() => setViewInvoice(f)} title="Voir les détails"><FaEye size={14}/></button>

                                        {f.status !== 'payée' && (
                                            <button className="btn btn-sm btn-outline-primary rounded-1 me-1 py-1 px-2" onClick={() => startEdit(f)} title="Modifier"><FaEdit size={14}/></button>
                                        )}
                                        
                                        <button 
                                            className="btn btn-sm btn-outline-danger rounded-1 me-1 py-1 px-2"
                                            onClick={() => handleDownloadPDF(f._id, f.number)} 
                                            title="Télécharger PDF"
                                            disabled={downloadingPdfId === f._id}
                                        >
                                            {downloadingPdfId === f._id ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : <FaFilePdf size={14}/>}
                                        </button>

                                        {(user?.role === 'admin' && f.status !== 'payée') && (
                                            <button className="btn btn-sm btn-outline-secondary rounded-1 py-1 px-2" onClick={() => handleDelete(f)} title="Supprimer (Admin)"><FaTrash size={14}/></button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* MODAL POUR VISUALISER LA FACTURE (CLAIRE) */}
            {viewInvoice && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content border-0 shadow">
                            <div className="modal-header bg-light py-3 border-bottom">
                                <h5 className="modal-title fs-6 fw-bold text-uppercase text-primary" style={{ letterSpacing: '1px' }}>Détails Facture : {viewInvoice.number}</h5>
                                <button type="button" className="btn-close" onClick={() => setViewInvoice(null)}></button>
                            </div>
                            <div className="modal-body p-4">
                                <div className="row mb-4">
                                    <div className="col-12 col-md-6 mb-3 mb-md-0">
                                        <h6 className="fw-bold small mb-1 text-muted">CLIENT FACTURÉ</h6>
                                        <div className="fs-6 fw-bold text-dark">{viewInvoice.clientName}</div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <h6 className="fw-bold small mb-1 text-muted">DATE ÉMISSION</h6>
                                        <div className="fw-bold text-dark">{new Date(viewInvoice.date).toLocaleDateString('fr-FR')}</div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <h6 className="fw-bold small mb-1 text-muted">STATUT</h6>
                                        <span className={`badge rounded-1 px-3 py-2 border ${viewInvoice.status === 'payée' ? 'bg-success text-success bg-opacity-10 border-success' : 'bg-warning text-warning-emphasis bg-opacity-10 border-warning'}`}>
                                            {viewInvoice.status.toUpperCase()}
                                        </span>
                                    </div>
                                </div>

                                <div className="table-responsive border rounded">
                                    <table className="table table-sm mb-0 table-hover align-middle">
                                        <thead className="bg-light small text-uppercase text-muted">
                                            <tr>
                                                <th className="ps-3 py-2 border-bottom-0">Prestation / Libellé</th>
                                                <th className="text-end py-2 border-bottom-0">P.U (FCFA)</th>
                                                <th className="text-center py-2 border-bottom-0">Qté</th>
                                                <th className="text-end pe-3 py-2 border-bottom-0">Total (FCFA)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {viewInvoice.lines?.map((l, i) => (
                                                <tr key={i}>
                                                    <td className="ps-3 py-2 text-wrap" style={{minWidth: '200px'}}>
                                                        <div className="fw-bold text-dark">{l.name}</div>
                                                        {l.details && <div className="text-muted small fst-italic">{l.details}</div>}
                                                    </td>
                                                    <td className="text-end py-2 text-dark">{l.price?.toLocaleString()}</td>
                                                    <td className="text-center py-2 text-dark">{l.quantity}</td>
                                                    <td className="text-end pe-3 py-2 fw-bold text-primary">{l.total?.toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className="bg-light">
                                                <td colSpan="3" className="text-end py-2 fw-bold text-muted">SOUS-TOTAL :</td>
                                                <td className="text-end pe-3 py-2 fw-bold text-secondary">
                                                    {(viewInvoice.totalHT + (viewInvoice.remise || 0)).toLocaleString()} F
                                                </td>
                                            </tr>
                                            <tr className="bg-light text-danger">
                                                <td colSpan="3" className="text-end py-2 fw-bold">REMISE :</td>
                                                <td className="text-end pe-3 py-2 fw-bold">-{viewInvoice.remise?.toLocaleString()} F</td>
                                            </tr>
                                            <tr className="bg-light">
                                                <td colSpan="3" className="text-end py-3 fw-bold text-dark">MONTANT TOTAL TTC :</td>
                                                <td className="text-end pe-3 py-3 fs-5 fw-bold text-primary">{viewInvoice.totalTTC?.toLocaleString()} F</td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                            <div className="modal-footer py-3 border-top bg-light">
                                <button className="btn btn-sm btn-secondary px-4 fw-bold" onClick={() => setViewInvoice(null)}>FERMER</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FacturePage;